'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, AlertCircle, Info, Loader2 } from 'lucide-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

type DialogVariant = 'danger' | 'warning' | 'info';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  variant?: DialogVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  /** Affiche uniquement le bouton de confirmation (remplace alert()). */
  hideCancel?: boolean;
  overlayClassName?: string;
}

interface VariantConfig {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  confirmClass: string;
}

const variantConfig: Record<DialogVariant, VariantConfig> = {
  danger: {
    icon: <AlertTriangle className="h-6 w-6" />,
    iconBg: 'bg-red-500/10',
    iconColor: 'text-red-600 dark:text-red-400',
    /**
     * `Button` défaut impose `dark:bg-gray-800` : couleurs explicites avec `!` pour
     * garantir fond + texte blanc lisible en clair comme en sombre.
     */
    confirmClass:
      '!bg-red-600 !text-white hover:!bg-red-700 active:!bg-red-800 shadow-sm dark:!bg-red-600 dark:hover:!bg-red-700',
  },
  warning: {
    icon: <AlertCircle className="h-6 w-6" />,
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-600 dark:text-amber-400',
    confirmClass:
      '!bg-amber-600 !text-white hover:!bg-amber-700 active:!bg-amber-800 shadow-sm dark:!bg-amber-600 dark:hover:!bg-amber-700',
  },
  info: {
    icon: <Info className="h-6 w-6" />,
    iconBg: 'bg-primary-900/10',
    iconColor: 'text-primary-900 dark:text-primary-200',
    confirmClass:
      '!bg-primary-900 !text-white hover:!bg-primary-700 active:!bg-primary-950 shadow-sm dark:!bg-primary-100 dark:!text-primary-900 dark:hover:!bg-primary-200',
  },
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  variant = 'danger',
  confirmLabel,
  cancelLabel,
  loading = false,
  hideCancel = false,
  overlayClassName,
}) => {
  const { t } = useTranslation();
  const config = variantConfig[variant];

  const resolvedConfirmLabel =
    confirmLabel ?? (hideCancel ? t('confirmDialog.ok', 'OK') : t('confirmDialog.confirm', 'Confirm'));
  const resolvedCancelLabel =
    cancelLabel ?? t('confirmDialog.cancel', 'Cancel');

  return (
    <Modal open={open} onClose={onClose} overlayClassName={overlayClassName}>
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full',
              config.iconBg,
              config.iconColor
            )}
          >
            {config.icon}
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        </div>
      </ModalHeader>

      <ModalBody>
        <p className="text-sm leading-relaxed text-gray-800 dark:text-zinc-200">{message}</p>
      </ModalBody>

      <ModalFooter>
        <div className="flex justify-end gap-3">
          {!hideCancel && (
            <Button
              onClick={onClose}
              disabled={loading}
              className="rounded-lg border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-100 dark:hover:bg-zinc-600 transition-colors"
            >
              {resolvedCancelLabel}
            </Button>
          )}
          <Button
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors',
              config.confirmClass,
              loading && 'cursor-not-allowed opacity-85'
            )}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {resolvedConfirmLabel}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmDialog;
